export interface ValidationErrors {
  [key: string]: string;
}

export interface ScheduleSlot {
  id?: string;
  time: string;
  isAvailable: boolean;
  isBooked?: boolean;
}

export interface DateSpecificAvailability {
  [date: string]: Array<{
    time: string;
    isAvailable: boolean;
  }>;
}

export interface BusinessProfileFormData {
  id?: string;
  nip: string;
  companyName: string;
  displayName: string;
  address: string;
  city: string;
  postalCode: string;
  email: string;
  phoneNumber: string;
  phoneCountry?: string;
  regon: string;
  krs: string;
  legalForm: number;
  categoryId: number;
  website: string;
  websiteDescription?: string;
  contactPersonName: string;
  contactPersonSurname: string;
  latitude?: number | null;
  longitude?: number | null;
  avatarUrl?: string | null;
  facilityPlanUrl?: string | null;
  facilityPlanFileName?: string | null;
  termsAndConditionsUrl?: string;
  autoConfirmTrainerAssociations?: boolean;
  // Schedule data
  weekdaysSchedule?: ScheduleSlot[];
  saturdaySchedule?: ScheduleSlot[];
  sundaySchedule?: ScheduleSlot[];
  dateSpecificAvailability?: DateSpecificAvailability;
}

/**
 * Validate NIP (Polish tax identification number)
 * Must be exactly 10 digits
 */
function validateNip(nip: string): boolean {
  if (!nip || !/^\d{10}$/.test(nip.trim())) {
    return false;
  }

  // NIP checksum validation
  const weights = [6, 5, 7, 2, 3, 4, 5, 6, 7];
  const digits = nip.split('').map(Number);
  const sum = weights.reduce((acc, weight, idx) => acc + weight * digits[idx], 0);
  const checksum = sum % 11;

  return checksum === digits[9];
}

/**
 * Validate REGON (Polish business registry number)
 * Must be 9 or 14 digits
 */
function validateRegon(regon: string): boolean {
  if (!regon || regon.trim() === '') return true; // Optional field
  return /^(\d{9}|\d{14})$/.test(regon.trim());
}

/**
 * Validate KRS (Polish court registry number)
 * Must be exactly 10 digits
 */
function validateKrs(krs: string): boolean {
  if (!krs || krs.trim() === '') return true; // Optional field
  return /^\d{10}$/.test(krs.trim());
}

/**
 * Validate Polish postal code format (XX-XXX)
 */
function validatePostalCode(postalCode: string): boolean {
  if (!postalCode || postalCode.trim() === '') return false;
  // Accept both XX-XXX and XXXXX formats
  return /^(\d{2}-\d{3}|\d{5})$/.test(postalCode.trim());
}

/**
 * Validate email format
 */
function validateEmail(email: string): boolean {
  if (!email || email.trim() === '') return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
}

/**
 * Validate URL format
 */
function validateUrl(url: string): boolean {
  if (!url || url.trim() === '') return false;
  return /^https?:\/\/.+/.test(url.trim());
}

/**
 * Validate a single field
 */
export function validateField(
  fieldName: keyof BusinessProfileFormData,
  value: unknown,
  currentErrors: ValidationErrors
): ValidationErrors {
  const newErrors = { ...currentErrors };
  const stringValue = typeof value === 'string' ? value : String(value ?? '');

  switch (fieldName) {
    case 'nip':
      if (!stringValue || stringValue.trim() === '') {
        newErrors.nip = 'NIP is required';
      } else if (!validateNip(stringValue)) {
        newErrors.nip = 'Invalid NIP format or checksum';
      } else {
        delete newErrors.nip;
      }
      break;

    case 'companyName':
      if (!stringValue || stringValue.trim() === '') {
        newErrors.companyName = 'Company name is required';
      } else if (stringValue.trim().length < 2) {
        newErrors.companyName = 'Company name must be at least 2 characters';
      } else {
        delete newErrors.companyName;
      }
      break;

    case 'displayName':
      if (!stringValue || stringValue.trim() === '') {
        newErrors.displayName = 'Display name is required';
      } else if (stringValue.trim().length < 2) {
        newErrors.displayName = 'Display name must be at least 2 characters';
      } else {
        delete newErrors.displayName;
      }
      break;

    case 'address':
      if (!stringValue || stringValue.trim() === '') {
        newErrors.address = 'Address is required';
      } else {
        delete newErrors.address;
      }
      break;

    case 'city':
      if (!stringValue || stringValue.trim() === '') {
        newErrors.city = 'City is required';
      } else {
        delete newErrors.city;
      }
      break;

    case 'postalCode':
      if (!stringValue || stringValue.trim() === '') {
        newErrors.postalCode = 'Postal code is required';
      } else if (!validatePostalCode(stringValue)) {
        newErrors.postalCode = 'Invalid postal code format (XX-XXX)';
      } else {
        delete newErrors.postalCode;
      }
      break;

    case 'email':
      if (!stringValue || stringValue.trim() === '') {
        newErrors.email = 'Email is required';
      } else if (!validateEmail(stringValue)) {
        newErrors.email = 'Invalid email format';
      } else {
        delete newErrors.email;
      }
      break;

    case 'phoneNumber':
      if (!stringValue || stringValue.trim() === '') {
        newErrors.phoneNumber = 'Phone number is required';
      } else if (stringValue.trim().length < 9) {
        newErrors.phoneNumber = 'Phone number must be at least 9 digits';
      } else {
        delete newErrors.phoneNumber;
      }
      break;

    case 'regon':
      if (!validateRegon(stringValue)) {
        newErrors.regon = 'REGON must be 9 or 14 digits';
      } else {
        delete newErrors.regon;
      }
      break;

    case 'krs':
      if (!validateKrs(stringValue)) {
        newErrors.krs = 'KRS must be exactly 10 digits';
      } else {
        delete newErrors.krs;
      }
      break;

    case 'legalForm':
      if (!value || value === 0) {
        newErrors.legalForm = 'Legal form is required';
      } else {
        delete newErrors.legalForm;
      }
      break;

    case 'categoryId':
      if (!value || value === 0) {
        newErrors.categoryId = 'Category is required';
      } else {
        delete newErrors.categoryId;
      }
      break;

    case 'website':
      if (!stringValue || stringValue.trim() === '') {
        newErrors.website = 'Website URL is required';
      } else if (!validateUrl(stringValue)) {
        newErrors.website = 'Invalid URL format (must start with http:// or https://)';
      } else {
        delete newErrors.website;
      }
      break;

    case 'contactPersonName':
      if (!stringValue || stringValue.trim() === '') {
        newErrors.contactPersonName = 'Contact person name is required';
      } else {
        delete newErrors.contactPersonName;
      }
      break;

    case 'contactPersonSurname':
      if (!stringValue || stringValue.trim() === '') {
        newErrors.contactPersonSurname = 'Contact person surname is required';
      } else {
        delete newErrors.contactPersonSurname;
      }
      break;

    case 'latitude':
      if (value !== null && value !== undefined && value !== '') {
        const lat = Number(value);
        if (isNaN(lat) || lat < -90 || lat > 90) {
          newErrors.latitude = 'Latitude must be between -90 and 90';
        } else {
          delete newErrors.latitude;
        }
      } else {
        delete newErrors.latitude;
      }
      break;

    case 'longitude':
      if (value !== null && value !== undefined && value !== '') {
        const lng = Number(value);
        if (isNaN(lng) || lng < -180 || lng > 180) {
          newErrors.longitude = 'Longitude must be between -180 and 180';
        } else {
          delete newErrors.longitude;
        }
      } else {
        delete newErrors.longitude;
      }
      break;
  }

  return newErrors;
}

/**
 * Validate entire form
 */
export function validateForm(formData: BusinessProfileFormData): ValidationErrors {
  let errors: ValidationErrors = {};

  // Required fields
  const requiredFields: (keyof BusinessProfileFormData)[] = [
    'nip', 'companyName', 'displayName', 'address', 'city', 'postalCode',
    'email', 'phoneNumber', 'legalForm', 'categoryId', 'website',
    'contactPersonName', 'contactPersonSurname'
  ];

  // Optional fields with validation
  const optionalFields: (keyof BusinessProfileFormData)[] = [
    'regon', 'krs', 'latitude', 'longitude'
  ];

  for (const field of [...requiredFields, ...optionalFields]) {
    errors = validateField(field, formData[field], errors);
  }

  return errors;
}

/**
 * Get empty form data
 */
export function getEmptyFormData(): BusinessProfileFormData {
  return {
    nip: '',
    companyName: '',
    displayName: '',
    address: '',
    city: '',
    postalCode: '',
    email: '',
    phoneNumber: '',
    phoneCountry: 'PL',
    regon: '',
    krs: '',
    legalForm: 0,
    categoryId: 0,
    website: '',
    websiteDescription: '',
    contactPersonName: '',
    contactPersonSurname: '',
    latitude: null,
    longitude: null,
    avatarUrl: null,
    facilityPlanUrl: null,
    facilityPlanFileName: null,
    termsAndConditionsUrl: '',
    autoConfirmTrainerAssociations: false,
    weekdaysSchedule: [],
    saturdaySchedule: [],
    sundaySchedule: [],
    dateSpecificAvailability: {},
  };
}
